import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import { getInputStyles } from "../../utils/themeUtils";
import config from "../../api/config";
import { getHeaders, getTenantId } from "../../api/apiHelpers";
import {
  setAuthzFromCapabilities,
  setIsMember,
  setIsStaff,
  setUser,
} from "../../store/slices/authSlice";
import { getCapabilities } from "../../api/authAPI";

/**
 * Inline sign-in form rendered when the anonymous chat detects the
 * visitor's email belongs to an existing member. Replaces the hard
 * redirect-to-signin pattern: the visitor authenticates without
 * leaving the conversation, and the chat continues with their
 * member context attached.
 *
 * On success this calls the SAME /auth/login endpoint Login.tsx uses
 * and dispatches the SAME Redux actions, so the rest of the app
 * (FloatingChatGate's auth check, the header user menu, permissions,
 * etc.) recognizes the user as authenticated immediately.
 *
 * After auth, we call onAuthSuccess() so the chat can send its
 * "continue" turn carrying the auth header — no message rendered to
 * the visitor; they just see the AI seamlessly pick back up.
 */
interface Props {
  emailPrefill?: string;
  accentColor: string;
  disabled?: boolean;
  onAuthSuccess: () => void;
}

const normalizeRole = (role: string): string =>
  role.toLowerCase().replace(/\s+/g, "_");

const ADMIN_ROLES = new Set([
  "admin",
  "super_admin",
  "tenant_owner",
  "platform_admin",
]);

const isAdminRole = (role: string | undefined): boolean => {
  if (!role) return false;
  return ADMIN_ROLES.has(normalizeRole(role));
};

const isStaffRole = (role: string | undefined): boolean => {
  if (!role) return false;
  const nonStaffRoles = ["member", "customer", "kiosk"];
  return !isAdminRole(role) && !nonStaffRoles.includes(role);
};

const AnonChatSigninField: React.FC<Props> = ({
  emailPrefill,
  accentColor,
  disabled,
  onAuthSuccess,
}) => {
  const { isDarkMode } = useTheme();
  const dispatch = useDispatch();
  const [email, setEmail] = useState(emailPrefill || "");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tenantId = getTenantId();

  const inputClass = `w-full p-2 rounded border text-sm ${getInputStyles(isDarkMode)}`;

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!email || !password || submitting || disabled) return;
    setError(null);
    setSubmitting(true);
    try {
      const resp = await fetch(`${config.BASE_URL}/auth/login`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ email, password }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError(data.error || "Invalid email or password.");
        setSubmitting(false);
        return;
      }
      const { token, user, source } = data;
      // Mirror Login.tsx — the rest of the app keys off these
      // localStorage entries + Redux state.
      localStorage.setItem("authToken", token);
      if (source) localStorage.setItem("authSource", source);
      dispatch(setUser(user));
      const userRole = (user?.role || "").toLowerCase();
      if (userRole) {
        localStorage.setItem("userRole", userRole);
        localStorage.setItem("userDetails", JSON.stringify(user));
        // Staff-vs-member comes from the role text, which is reliable for that
        // distinction. The ADMIN bit does not: it must come from the server,
        // which resolves it from role_id rather than the drifted users.role.
        // This path previously set isAdmin from the string and never fetched
        // capabilities at all, so anyone signing in through the chat kept a
        // string-derived admin bit for the rest of the session.
        if (isStaffRole(userRole)) dispatch(setIsStaff(userRole));
        else if (!isAdminRole(userRole)) dispatch(setIsMember(userRole));

        try {
          const caps = await getCapabilities();
          if (caps) dispatch(setAuthzFromCapabilities(caps));
        } catch {
          // Leave isAdmin false. The gate fails closed; the backend refuses
          // anything this caller may not do regardless of what the UI shows.
        }
      }
      setSubmitting(false);
      // Hand control back to the chat — it'll send a continuation
      // turn with the auth header now in place.
      onAuthSuccess();
    } catch (err: any) {
      setError(err?.message || "Sign in failed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="px-3 pt-2 pb-1 space-y-2">
      <p className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
        Please sign in to continue.
      </p>
      <input
        aria-label="Email"
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email"
        disabled={submitting || disabled}
        className={inputClass}
      />
      <input
        aria-label="Password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Password"
        disabled={submitting || disabled}
        className={inputClass}
      />
      {error && <p className="text-xs text-red-500">{error}</p>}
      <div className="flex items-center justify-between gap-2">
        <button
          type="submit"
          disabled={submitting || disabled || !email || !password}
          className="px-4 py-1.5 rounded text-sm text-[var(--cc-action-text)] disabled:opacity-50"
          style={{ backgroundColor: accentColor }}
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>
        <Link
          to={tenantId ? `/${tenantId}/forgot-password` : "/forgot-password"}
          className={`text-[11px] underline ${
            isDarkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Forgot password?
        </Link>
      </div>
    </form>
  );
};

export default AnonChatSigninField;
